import * as React from 'react';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-dropdown-select';
import { getLocalization, globalWindow } from 'global/global';
import { useAppDispatch, useAppSelector } from 'index';
import { AdminGroup, AdminGroupDraft } from 'Interfaces/Admin';
import { showAlert, showConfirm } from 'actions';
import { saveAdminGroup } from 'actions/adminGroupActions';
import { openHtmlZipUrl } from 'api/adminApi';
import { adminGroupsSelector, isSuperUserChild } from 'reducers/admin/selectors';
import { showColumns } from 'reducers/admin/ui';
import { GROUP_SHARING_TOGGLES, GROUP_TOGGLES, NEW_GROUP_RIGHTS, RIGHTS_ROWS } from './rights';
import { buildGroupPayload, groupFormVisibility, validateGroup } from './payload';
import { useHtmlZip } from './useHtmlZip';

interface Props {
  /** null when adding a new group. */
  groupId: number | null;
}

const EMPTY_DRAFT: AdminGroupDraft = { name: '', active: true, ...NEW_GROUP_RIGHTS };

/** Replaces GroupView.js. */
export default function GroupForm(props: Props) {
  const dispatch = useAppDispatch();
  const groups = useAppSelector(adminGroupsSelector);
  const { formLists, forms, hierarchies } = useAppSelector((state) => state.admin.meta);
  const account = useAppSelector((state) => state.admin.account);

  const existing = props.groupId !== null
    ? groups.find((g) => g.id === props.groupId)
    : undefined;

  const [draft, setDraft] = React.useState<AdminGroupDraft>(existing || EMPTY_DRAFT);
  /**
   *  BG-2840: an empty parent field must not wipe an existing parent unless the
   *  user actually touched it, so the "was it edited" flag is tracked
   *  separately from the value.
   */
  const [parentTouched, setParentTouched] = React.useState(false);
  const fileInput = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setDraft(existing || { ...EMPTY_DRAFT });
    setParentTouched(false);
  }, [props.groupId]);

  const set = <K extends keyof AdminGroup>(field: K, value: AdminGroup[K]) =>
    setDraft((d) => ({ ...d, [field]: value }));

  const isNew = !existing;
  const isSuperUserGroup = existing?.superuser_group === true;

  const visibility = groupFormVisibility(isSuperUserGroup, account);

  const parentOptions = React.useMemo(
    () => groups.filter((g) => g.active
      && (isNew || (g.id !== existing?.id && existing?.id !== g.parentId))),
    [groups, existing, isNew],
  );

  const hierarchy = hierarchies.find((h) => h.id === draft.locationHierarchyId);
  const markerOptions = hierarchy
    ? (['location1', 'location2', 'location3', 'location4'] as const)
      .map((key) => ({ value: key, label: hierarchy[key] }))
      .filter((o) => !!o.label)
    : [];

  const surveyModel = formLists.find((f) => f.id === draft.surveyId);
  const zip = useHtmlZip(existing, draft, surveyModel, (patch) => setDraft((d) => ({ ...d, ...patch })));

  const verifyOrganizationParent = (parentId: number | null) => {
    if (!isSuperUserChild(groups, parentId)) {
      set('parentId', null);
      dispatch(showAlert({ visible: true, message: getLocalization('parentGroupAlert') }));
    }
  };

  const onOrganizationChange = (checked: boolean) => {
    set('organization', checked);
    if (checked) {
      set('parentId', account.groupId);
      verifyOrganizationParent(account.groupId);
    }
  };

  const onActiveChange = (checked: boolean) => {
    if (existing && existing.id === account.groupId) {
      dispatch(showAlert({ visible: true, message: getLocalization('groupActivateError') }));
      return;
    }
    if (!checked) {
      dispatch(showConfirm({
        visible: true,
        message: getLocalization('groupDeactivateConfirm'),
        onConfirm: () => set('active', false),
      }));
      return;
    }
    set('active', checked);
  };

  const onFormsChange = (identifiers: string[]) => {
    setDraft((d) => ({
      ...d,
      forms: identifiers,
      // Picking forms directly replaces any chosen form list.
      surveyId: null,
      formListName: `${d.name || ''}-list`,
    }));
  };

  const save = () => {
    const failure = validateGroup(draft);
    if (failure) {
      dispatch(showAlert({ visible: true, message: getLocalization(failure) }));
      return;
    }

    const payload = buildGroupPayload(draft, {
      existing,
      parentTouched,
      accountGroupId: account.groupId,
      accountIsOrganization: account.isOrganization,
      formList: surveyModel,
      htmlZip: zip.pendingZip,
    });
    const commit = () => {
      void dispatch(saveAdminGroup(payload, existing?.active)).then((result) => {
        if (result.saved && result.group) {
          dispatch(showColumns({ path: [{ type: 'group', id: result.group.id }] }));
        }
      });
    };

    // A group with neither forms nor a form list collects nothing.
    if (!payload.forms?.length && !payload.surveyId) {
      dispatch(showConfirm({
        visible: true,
        message: getLocalization('confirmNoForms'),
        onConfirm: commit,
      }));
      return;
    }
    commit();
  };

  const checkbox = (
    id: string, label: string, checked: boolean, onChange: (v: boolean) => void, disabled = false,
  ) => (
    <Form.Check
      id={id}
      label={<span dangerouslySetInnerHTML={{__html: getLocalization(label) }} />}
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
  );

  return (
    <div className="admin-form p-3">
      <Form onSubmit={(e) => e.preventDefault()}>
        {existing && (
          <div className="form-group">
            <Button size="sm" onClick={() => dispatch(showColumns({
              path: [{ type: 'group', id: existing.id }],
            }))}
            >
              {getLocalization('myGroups')}
            </Button>
          </div>
        )}

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('groupName')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            size="sm"
            value={draft.name || ''}
            onChange={(e) => set('name', e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('locationHierarchy')}</Form.Label>
          <Form.Control
            as="select"
            size="sm"
            value={draft.locationHierarchyId ?? ''}
            onChange={(e) => set('locationHierarchyId', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="" />
            {hierarchies.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </Form.Control>
        </Form.Group>

        <h5>{getLocalization('selectformformlist')}</h5>

        <Form.Group className="mb-2">
          <Form.Label title={getLocalization('formListInfo')}>{getLocalization('formList')}</Form.Label>
          <Form.Control
            as="select"
            size="sm"
            value={draft.surveyId ?? ''}
            onChange={(e) => setDraft((d) => ({
              ...d,
              surveyId: e.target.value ? Number(e.target.value) : null,
              formListName: null,
            }))}
          >
            <option value="" />
            {formLists.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </Form.Control>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('forms')}</Form.Label>
          <Select
            multi
            options={forms.map((f) => ({ identifier: f.identifier, name: f.name || '' }))}
            values={forms
              .filter((f) => draft.forms?.includes(f.identifier))
              .map((f) => ({ identifier: f.identifier, name: f.name || '' }))}
            onChange={(selected) => onFormsChange(selected.map((s) => s.identifier))}
            labelField="name"
            valueField="identifier"
            searchBy="name"
            searchable
            clearable
            create={false}
            placeholder=""
            className="form-control form-control-sm"
          />
        </Form.Group>

        {!draft.surveyId && !!draft.forms?.length && (
          <Form.Group className="mb-2">
            <Form.Label>{getLocalization('formlistName')} <span className="text-danger">*</span></Form.Label>
            <Form.Control
              size="sm"
              value={draft.formListName || ''}
              onChange={(e) => set('formListName', e.target.value)}
            />
          </Form.Group>
        )}

        <Form.Group className="mb-2">
          {!zip.hasZip && (
            <>
              <Button
                size="sm"
                disabled={!draft.forms?.length && !draft.surveyId}
                onClick={() => fileInput.current?.click()}
              >
                {getLocalization('attachHTML')}
              </Button>
              <input
                ref={fileInput}
                type="file"
                className="d-none"
                accept=".zip"
                onChange={(e) => zip.select(e.target.files?.[0])}
              />
            </>
          )}
          {zip.hasZip && (
            <>
              {!zip.pendingZip && (
                <Button
                  size="sm"
                  onClick={() => {
                    const target = zip.target();
                    if (target) {
                      globalWindow.location = openHtmlZipUrl(target.type, target.id);
                    }
                  }}
                >
                  {getLocalization('viewHTML')}
                </Button>
              )}
              <Button size="sm" variant="danger" onClick={() => void zip.remove()}>
                {getLocalization('removeHTML')}
              </Button>
            </>
          )}
        </Form.Group>

        {visibility.organization && (
          <Form.Group className="mb-2">
            {checkbox(
              'group-organization', 'isorganization', !!draft.organization, onOrganizationChange,
            )}
            <Form.Text>{getLocalization('groupOrganization')}</Form.Text>
          </Form.Group>
        )}

        {visibility.parent && (
          <>
            <Form.Group className="mb-2">
              <Form.Text>{getLocalization('grouphierarchynote')}</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>{getLocalization('parent')}</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                value={draft.parentId ?? ''}
                onChange={(e) => {
                  const parentId = e.target.value ? Number(e.target.value) : null;
                  setParentTouched(true);
                  set('parentId', parentId);
                  if (draft.organization && parentId) {
                    verifyOrganizationParent(parentId);
                  }
                }}
              >
                <option value="" />
                {parentOptions.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Form.Control>
            </Form.Group>
          </>
        )}

        <div className="row rights-header">
          <div className="col-4"><label>{getLocalization('editingrights')}</label></div>
          <div className="col-4"><label>{getLocalization('accessrights')}</label></div>
          <div className="col-4"><label>{getLocalization('deletionrights')}</label></div>
        </div>
        {RIGHTS_ROWS.map((row) => (
          <div className="row" key={row.edit}>
            {(['edit', 'read', 'remove'] as const).map((column) => {
              const field = row[column];
              const label = row[`${column === 'remove' ? 'remove' : column}Label`];
              return (
                <div className="col-4" key={`${row.edit}-${column}`}>
                  {checkbox(
                    `group-${String(field)}`,
                    label,
                    !!draft[field],
                    (v) => set(field, v as never),
                    row.disabled?.includes(column),
                  )}
                </div>
              );
            })}
          </div>
        ))}

        <hr />
        {GROUP_TOGGLES.map(({ field, label }) => (
          <React.Fragment key={String(field)}>
            {checkbox(`group-${String(field)}`, label, !!draft[field], (v) => set(field, v as never))}
          </React.Fragment>
        ))}
        {checkbox(
          'group-active', 'active', draft.active !== false, onActiveChange, visibility.activeLocked,
        )}

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('markerpreflabel')}</Form.Label>
          <Form.Control
            as="select"
            size="sm"
            value={draft.marker_preference || 'form'}
            onChange={(e) => set('marker_preference', e.target.value)}
          >
            <option value="form">{getLocalization('form')}</option>
            {markerOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Form.Control>
        </Form.Group>

        {GROUP_SHARING_TOGGLES.map(({ field, label }) => (
          <React.Fragment key={String(field)}>
            {checkbox(`group-${String(field)}`, label, !!draft[field], (v) => set(field, v as never))}
          </React.Fragment>
        ))}
      </Form>

      <div className="form-actions">
        <Button size="sm" onClick={save}>
          {getLocalization(existing ? 'update' : 'save')}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => dispatch(showConfirm({
            visible: true,
            message: getLocalization(existing ? 'confirmCancelEditGroup' : 'confirmCancelNewGroup'),
            onConfirm: () => { dispatch(showColumns({ path: [] })); },
          }))}
        >
          {getLocalization('cancel')}
        </Button>
      </div>
    </div>
  );
}
